import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MainService } from '../../services/main.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GmsContext } from '../../models/gms/gms-context';
import { MessageService } from '../../services/message.service';
import { Message, MessageType } from '../../models/gms/message';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { environment } from '../../../environments/environment';
import { AlarmManualCurves } from '../../models/gms/alarm-manual-curves';
import { PlotBand } from '../../models/gms/plot-band';
import { Series } from '../../models/gms/series';
import { GmsInfoDirective } from '../_shared/gms-info/gms-info.directive';
import { MessagesTabLabel } from './messages-tab-label.enum';
import { OfflineWarning } from '../../models/gms/offline-warning';
import { OfflineWarningService } from '../../services/offline-warning.service';
import { Subscription } from 'rxjs';
import { HttpResponse } from '@angular/common/http';

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class MessagesComponent extends GmsInfoDirective implements OnInit, OnDestroy {
  activeMessages: Message[] = [];
  offlineWarnings: OfflineWarning[] = [];
  dataSource = new MatTableDataSource([...this.activeMessages, ...this.offlineWarnings]);

  columnsToDisplay = ['select', 'engine', 'sender', 'shortTitle', 'severity', 'eventDate', 'delete'];
  expandedElement: Message | OfflineWarning | null;
  selection = new SelectionModel<Message | OfflineWarning>(true, []);
  index: number;
  currentTabLabel: MessagesTabLabel;
  subscriptions: Subscription[] = [];

  @ViewChild('tabs') tabGroup: MatTabGroup;

  constructor(public main: MainService,
              private route: ActivatedRoute,
              private readonly messageService: MessageService,
              private readonly offlineWarningService: OfflineWarningService,
              private dialog: MatDialog,
              public router: Router) {
    super(main, router);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.gmsId = this.route.snapshot.paramMap.get('gmsId');
    const messageType = this.route.snapshot.paramMap.get('type') as MessageType;
    const messageId = this.route.snapshot.paramMap.get('id');

    this.main.setActiveById(this.gmsId);
    this.loadAndShowMessages(this.main.activeItem, messageType, messageId);
    this.loadOfflineWarnings();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    super.ngOnDestroy();
  }

  loadAndShowMessages(gmsContext: GmsContext, messageType: MessageType, messageId: string): void {
    this.subscriptions.push(this.messageService.getAll(gmsContext.gmsId).subscribe(messages => {

      // set charts for every message
      messages.forEach(m => m.chart = this.generateHighchart(m.alarmManualCurves));

      this.updateUnreadMessageCounter();

      // directly open the tab if route param is set
      if (messageType) {
        this.activeMessages = messages.filter(m => m.messageType === messageType);

        switch (messageType) {
          case MessageType.Alarm:
            this.tabGroup.selectedIndex = 1;
            break;
          case MessageType.Warning:
            this.tabGroup.selectedIndex = 2;
            break;
          case MessageType.Info:
            this.tabGroup.selectedIndex = 4;
            break;
        }

        // directly open the message if route param is set
        if (messageId) {
          this.expandedElement = this.activeMessages.find(m => m.id === +messageId);
          if (this.expandedElement) {
            this.markMessageAsRead(this.expandedElement);
          }
        }

      } else {
        this.activeMessages = messages;
      }

      this.dataSource = new MatTableDataSource(this.sortByDate([...this.activeMessages, ...this.offlineWarnings]));
    }));
  }

  loadOfflineWarnings(): void {
    this.subscriptions.push(this.offlineWarningService.getAll(this.gmsId).subscribe(offlineWarnings => {
      this.offlineWarnings = offlineWarnings;
      this.dataSource = new MatTableDataSource(this.sortByDate([...this.activeMessages, ...this.offlineWarnings]));
    }));
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Message): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.row + 1}`;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onTabChange(e: MatTabChangeEvent) {
    this.currentTabLabel = e.tab.textLabel as MessagesTabLabel;

    this.loadElementsForTab();
  }

  countUnreadMessagesForType(messageType: MessageType | null): number {
    if (!messageType) {
      return this.activeMessages.filter(m => !m.read).length;
    }
    return this.activeMessages.filter(m => m.messageType === messageType && !m.read).length;
  }

  markMessageAsRead(element: Message | OfflineWarning): void {
    if ('read' in element)
    {
      element.read = true;
      this.updateUnreadMessageCounter();
      this.subscriptions.push(this.messageService.markAsRead(element.id).subscribe());
    }
  }

  delete(element: Message | OfflineWarning): void {
    const foundMessage = this.activeMessages.find(message => message === element);
    const foundOfflineWarning = this.offlineWarnings.find(offlineWarning => offlineWarning === element);

    if (foundMessage) {
      this.activeMessages = this.activeMessages.filter(message => message.id !== foundMessage.id);
      this.subscriptions.push(this.messageService.delete(foundMessage.id).subscribe());
    }
    if (foundOfflineWarning) {
      this.offlineWarnings = this.offlineWarnings.filter(offlineWarning => offlineWarning.id !== foundOfflineWarning.id);
      this.subscriptions.push(this.offlineWarningService.delete(this.gmsId, foundOfflineWarning.id).subscribe());
    }

    this.loadElementsForTab();
  }

  openDeleteSelectedMessagesDialog(nrOfMessages: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'Are you sure you want to delete all ' + nrOfMessages + ' selected messages?',
        buttonText: {
          ok: 'Yes, delete',
          cancel: 'Cancel',
        },
      },
    });

    this.subscriptions.push(dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        const foundMessages = this.activeMessages.filter(message => this.selection.selected.includes(message));
        const foundOfflineWarnings = this.offlineWarnings.filter(offlineWarnings => this.selection.selected.includes(offlineWarnings));

        if (foundMessages.length > 0) {
          this.activeMessages = this.activeMessages.filter(message => !this.selection.selected.includes(message));
          foundMessages.forEach(message => {
            this.subscriptions.push(this.messageService.delete(message.id).subscribe());
          });
        }
        if (foundOfflineWarnings.length > 0) {
          this.offlineWarnings = this.offlineWarnings.filter(offlineWarnings => !this.selection.selected.includes(offlineWarnings));
          foundOfflineWarnings.forEach(offlineWarning => {
            this.subscriptions.push(this.messageService.delete(offlineWarning.id).subscribe());
          });
        }

        this.selection.clear();

        this.loadElementsForTab();
      }
    }));
  }

  private updateUnreadMessageCounter(): void {
    this.main.activeItem.unreadMessagesCount = this.activeMessages.filter(m => !m.read).length;
  }

  createMailToLink(message: Message): string {
    return 'mailto:' + environment.supportMail +
      '?Subject=Support Request for GMS ' + message.gmsId + ': ' + message.longTitle +
      '&body=Hi,%0D%0A%0D%0AI have a question concerning the following ' + message.messageType + ' message:' + '%0D%0A' +
      window.location.href + '/' + message.messageType + '/' + message.id;
  }

  debug(message: Message): void {
    console.log(message);
  }

  sortByDate(elements: (Message | OfflineWarning)[]): (Message | OfflineWarning)[] {
    return elements.sort((a, b) => {
      const aDateValue = ('eventDate' in a ? a.eventDate : a.createdAt);
      const bDateValue = ('eventDate' in b ? b.eventDate : b.createdAt);

      const aDate = aDateValue instanceof Date ? aDateValue : new Date(aDateValue);
      const bDate = aDateValue instanceof Date ? aDateValue : new Date(bDateValue);

      return bDate.getTime() - aDate.getTime();
    });
  }

  hasShortTitle(element: Message | OfflineWarning) {
    return 'shortTitle' in element;
  }

  hasSubject(element: Message | OfflineWarning) {
    return 'subject' in element;
  }

  hasEventDate(element: Message | OfflineWarning) {
    return 'eventDate' in element;
  }

  hasCreatedAt(element: Message | OfflineWarning) {
    return 'createdAt' in element;
  }

  private loadElementsForTab() {
    switch (this.currentTabLabel ?? MessagesTabLabel.All) {
      case MessagesTabLabel.All:
        this.dataSource = new MatTableDataSource(this.sortByDate([...this.activeMessages, ...this.offlineWarnings]));
        return;
      case MessagesTabLabel.Alarms:
        this.dataSource = new MatTableDataSource(this.activeMessages.filter(message => message.messageType === MessageType.Alarm));
        return;
      case MessagesTabLabel.Warnings:
        this.dataSource = new MatTableDataSource(this.activeMessages.filter(message => message.messageType === MessageType.Warning));
        return;
      case MessagesTabLabel.Infos:
        this.dataSource = new MatTableDataSource(this.activeMessages.filter(message => message.messageType === MessageType.Info));
        return;
      case MessagesTabLabel.OfflineWarnings:
        this.dataSource = new MatTableDataSource(this.offlineWarnings);
        return;
    }
  }

  downloadAttachment(attachment: string) {
    this.subscriptions.push(this.offlineWarningService.downloadAttachment(this.gmsId, attachment).subscribe({
      next: res => this.openOrDownload(res),
      error: err => console.error('Download failed', err),
    }));
  }

  openOrDownload(res: HttpResponse<Blob>) {
    const blob = res.body!;
    const contentType = res.headers.get('Content-Type') || 'application/octet-stream';
    const contentDisposition = res.headers.get('Content-Disposition') || '';
    const filename = this.extractFilename(contentDisposition) || 'download';

    const previewable =
      contentType.startsWith('application/pdf') ||
      contentType.startsWith('image/') ||
      contentType.startsWith('audio/') ||
      contentType.startsWith('video/') ||
      contentType.startsWith('text/');

    const url = URL.createObjectURL(blob);

    if (previewable) {
      const win = window.open(url, '_blank');
      if (!win) {
        // Fallback to download if pop-up blocked
        this.forceDownload(url, filename);
      } else {
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } else {
      this.forceDownload(url, filename);
    }
  }

  private forceDownload(url: string, filename: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke soon after to free memory
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private extractFilename(contentDisposition: string): string | null {
    const fileNameStar = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
    if (fileNameStar?.[1]) return decodeURIComponent(fileNameStar[1]);

    const fileName = /filename\s*=\s*"([^"]+)"/i.exec(contentDisposition)
      || /filename\s*=\s*([^;]+)/i.exec(contentDisposition);
    return fileName?.[1]?.trim() ?? null;
  }

  generateHighchart(alarmManualCurves: AlarmManualCurves): any {
    if (!alarmManualCurves) {
      return;
    }

    const xPlotBands = MessagesComponent.parsePlotBands(alarmManualCurves.xAxis?.plotBands);
    const yPlotBands = MessagesComponent.parsePlotBands(alarmManualCurves.yAxis?.plotBands);
    const xPlotLines = MessagesComponent.parsePlotLines(alarmManualCurves.xAxis?.plotLineValues);
    const yPlotLines = MessagesComponent.parsePlotLines(alarmManualCurves.yAxis?.plotLineValues);
    const series = MessagesComponent.parseSeries(alarmManualCurves.series);

    // console.log(chart);

    return {
      id: alarmManualCurves.title,
      chart: {
        // styledMode: true,
        zoomType: 'x',
        panning: true,
        panKey: 'shift',
        resetZoomButton: { position: { x: 0, y: -30 } },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        positioner() {
          return { x: 0, y: 0 };
        },
      },
      credits: { enabled: false },
      legend: { enabled: alarmManualCurves.legendEnabled },
      title: { text: alarmManualCurves.title },
      defs: {
        gradientWhiteTransparent: {
          tagName: 'linearGradient',
          id: 'gradientWhiteTransparent', x1: 0, y1: 0,
          children: [{
            tagName: 'stop',
            offset: 0,
          }, {
            tagName: 'stop',
            offset: 1,
          }],
        },
      },
      xAxis: [{
        title: {
          text: alarmManualCurves.xAxis?.title,
        },
        plotBands: xPlotBands,
        plotLines: xPlotLines,
      }],
      yAxis: [{
        title: {
          text: alarmManualCurves.yAxis?.title,
        },
        plotBands: yPlotBands,
        plotLines: yPlotLines,
      }],
      series,
    };
  }

  private static parsePlotBands(plotBands: PlotBand[]): any[] {
    if (!plotBands) {
      return [];
    }

    const axisPlotBands = [];
    for (const pb of plotBands) {
      axisPlotBands.push({
        color: pb.color,
        from: pb.from,
        to: pb.to,
        label: {
          text: pb.labelText,
          align: 'center',
          color: pb.labelColor,
        },
      });
    }
    return axisPlotBands;
  }

  private static parsePlotLines(plotLineValues: number[]): any[] {
    if (!plotLineValues) {
      return [];
    }

    const axisPlotLines = [];
    for (const value of plotLineValues) {
      axisPlotLines.push({ value });
    }
    return axisPlotLines;
  }

  private static parseSeries(ser: Series[]): any[] {
    if (!ser) {
      return [];
    }

    const series = [];
    for (const s of ser) {
      series.push({
        type: s.type,
        name: s.name,
        data: MainService.combineData(s.dataX, s.dataY),
        color: s.color,
        dashStyle: s.dashStyle,
      });
    }
    return series;
  }

  protected readonly MessagesTabLabel = MessagesTabLabel;
  protected readonly MessageType = MessageType;
}
